/** Grade dropdown options from the ACTIVE institution's grade scale (W43 P3, #275).
 *
 *  Replaces the hard-coded UMD `GRADE_OPTIONS` list: SJSU records CR/NC/WU,
 *  UC Merced P/NP/S/U — a UMD list let students pick marks their registrar
 *  does not issue and hid the ones it does. Labels for the common special
 *  marks are spelled out; anything else renders as its code.
 */
import { useMemo } from 'react';
import { useInstitutionConfig } from '@/contexts/InstitutionConfigContext';
import { UMD_GRADE_MARKS } from '@/fallbacks/umdGradeMarks';
import type { GradeOption } from '@/components/shared/modals/UnifiedCourseModal/constants';

const MARK_LABELS: Record<string, string> = {
  P: 'P (Pass)',
  NP: 'NP (No Pass)',
  CR: 'CR (Credit)',
  NC: 'NC (No Credit)',
  S: 'S (Satisfactory)',
  U: 'U (Unsatisfactory)',
  W: 'W (Withdrawn)',
  WU: 'WU (Unauthorized Withdrawal)',
  I: 'I (Incomplete)',
  IP: 'IP (In Progress)',
  XF: 'XF (Failure — academic dishonesty)',
};

export const buildGradeOptions = (marks: readonly string[]): GradeOption[] => [
  { value: '', label: 'No Grade' },
  ...marks.map((m) => ({ value: m, label: MARK_LABELS[m] ?? m })),
];

export const useGradeOptions = (): GradeOption[] => {
  // Tests and a few legacy mounts render without the provider; a cached
  // pre-P3 config also lacks the field. Both fall back to the UMD marks.
  const ctx = useInstitutionConfig();
  const marks = ctx?.config?.gradeMarks ?? UMD_GRADE_MARKS;
  return useMemo(() => buildGradeOptions(marks), [marks]);
};
